import React, { useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { getVatInfo } from '../../services/apis';
import { Input } from '../shared/Input';
import { Button } from 'primereact/button';
import { regexValidation } from '../../constants/regexValidation';

import './fields.css';
import { OverlayPanel } from 'primereact/overlaypanel';
import { useTranslation } from 'react-i18next';

const BAHRAIN_VAT_PREFIX_REGEX = /^(20|21|22)\d{13}$/;

const isValidVatNumber = (vatNumber, hasError) => {
  const trimmedVatNumber = vatNumber?.trim();

  return (
    Boolean(trimmedVatNumber) &&
    hasError !== true &&
    regexValidation.vatNumber.test(trimmedVatNumber) &&
    BAHRAIN_VAT_PREFIX_REGEX.test(trimmedVatNumber)
  );
};

const VatInputField = ({
  field,
  form,
  updateFieldsNames,
  verifyVat,
  forceVerifyVat,
  ...restProps
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState();
  const debouncedFetchRef = useRef(null);
  const op = useRef(null);

  // Create debounced API call function
  useEffect(() => {
    debouncedFetchRef.current = debounce(async (vatNumber, e) => {
      const trimmedVatNumber = vatNumber?.trim();

      if (!isValidVatNumber(trimmedVatNumber, restProps.hasError)) {
        return;
      }

      setLoading(true);

      try {
        const response = await getVatInfo(trimmedVatNumber);

        if (response?.data) {
          // Update other fields with VAT info if provided
          if (response.data.name) {
            setName(response.data.name);
            verifyVat && e && op?.current?.toggle(e);
            // You can map this to appropriate fields based on your form structure
            form.setFieldValue(updateFieldsNames.name, response.data.name);
            form.setFieldValue(
              updateFieldsNames.phoneNumber,
              response.data.phoneNumber,
            );
            form.setFieldValue(updateFieldsNames.email, response.data.email);
            form.setFieldValue(
              updateFieldsNames.streetName,
              response.data.street,
            );
            form.setFieldValue(updateFieldsNames.block, response.data.block);
            form.setFieldValue(
              updateFieldsNames.buildingName,
              response.data.strSuppl1,
            );
            form.setFieldValue(
              updateFieldsNames.flatNumber,
              response.data.houseNumber,
            );
            form.setFieldValue(updateFieldsNames.city, response.data.city);
            form.setFieldValue(
              updateFieldsNames.country,
              response.data.country,
            );
            form.setFieldValue(
              updateFieldsNames.tin,
              trimmedVatNumber.substring(0, 10),
            );
          }
        }
      } catch (error) {
        console.error('Error fetching VAT info:', error);
      } finally {
        setLoading(false);
      }
    }, 500); // 500ms debounce delay

    return () => {
      if (debouncedFetchRef.current) {
        debouncedFetchRef.current.cancel();
      }
    };
  }, [verifyVat, forceVerifyVat]);

  // Watch field.value and trigger debounced API call
  useEffect(() => {
    if (
      (!verifyVat || forceVerifyVat) &&
      debouncedFetchRef.current &&
      isValidVatNumber(field.value, restProps.hasError)
    ) {
      debouncedFetchRef.current(field.value);
      return;
    }

    debouncedFetchRef.current?.cancel();
  }, [field.value, verifyVat, forceVerifyVat, restProps.hasError]);

  return (
    <div className="vat-input-container d-flex align-items-center">
      <Input
        {...field}
        {...restProps}
        className="vat-input"
        disabled={loading || restProps?.disabled}
      />
      {loading && <i className="ms-2 pi pi-spin pi-spinner" />}
      {verifyVat && (
        <Button
          label={t('verify-vat-number')}
          text
          link
          type="button"
          className="verify-vat-btn"
          disabled={loading || restProps.hasError}
          onClick={(e) => {
            if (!isValidVatNumber(field.value, restProps.hasError)) {
              return;
            }

            debouncedFetchRef.current(field.value, e);
          }}
        />
      )}
      <OverlayPanel
        ref={op}
        showCloseIcon
        closeOnEscape
        dismissable={false}
        className="vat-input-overlay rounded-4 p-4"
      >
        <div className="d-flex flex-column align-items-start justify-content-start gap-3">
          <b>
            {t('vat-number-associated-with')} {name}
          </b>
          <Button
            icon="pi pi-copy"
            label={t('copy-customer-name')}
            text
            onClick={() => navigator.clipboard.writeText(name)}
          />
        </div>
      </OverlayPanel>
    </div>
  );
};

export default React.memo(VatInputField);
